import React, {useCallback, useMemo, useState} from 'react';
import './documentContent/highlights.css';
import {Stack} from "@mui/material";
import {useSession} from "../firebase/UserProvider";
import useDocumentTags from "./documentContent/useDocumentTags";
import EntityDetailsDialog from "./EntityDetailsDialog";
import WikidataEntitySearchDialog from "./prosemirror-editor/components/EntitySearchDialog";
import {useMultipleSegmentEntities} from "./documentContent/useSegmentEntities";
import {useScrollToSegment} from './documentContent/useScrollToSegment';
import {useDocumentFeedback} from './documentContent/useDocumentFeedback';
import {useHighlightsBySegment} from './documentContent/useHighlightsBySegment';
import {useDisplaySegments} from './documentContent/useDisplaySegments';
import {useEntityDialog} from './documentContent/useEntityDialog';
import {useEffectiveQuery} from './documentContent/useEffectiveQuery';
import {useCssStylesInjection} from './documentContent/useCssStylesInjection';
import DocumentHeader from './documentContent/DocumentHeader';
import SegmentList from './documentContent/SegmentList';
import {MentionHoverDismissProvider} from './entityMentions/MentionHoverDismissContext';
import useSourcePackageBuilderStore from '../stores/useSourcePackageBuilderStore';
import {linkSegmentEntity} from '../utils/api/entities';
import {isLocalEntityRef} from '../utils/entityLinks';

// Memoized document content component
const DocumentContent = React.memo(({
                                        props,
                                        savedHighlights = [],
                                        searchResultSegmentIds = [],
                                        activeSegmentIndex = null,
                                        viewMode = 'text',
                                        setViewMode = () => {
                                        },
                                        htmlSegments = null,
                                        loadingHtml = false
                                    }) => {
    const {
        docId,
        docTitle,
        docSource,
        docSourceType,
        docUrl,
        sourceScore,
        feedback,
        classificationLevel,
        segments = [],
        segmentsWithMetadata = [],
        tags: incomingTags = [],
        hasHTML = false,
        cssStyles = null
    } = props ?? {};

    const session = useSession();
    const user = session?.user;

    // Get segmentIndex from Zustand store (synchronized with documentId)
    const segmentIndex = useSourcePackageBuilderStore(s => s.segmentIndex);
    const effectiveSegmentIndex = activeSegmentIndex ?? segmentIndex;

    // Custom hooks for state and data management
    const displaySegments = useDisplaySegments({segments, segmentsWithMetadata, docId});
    const effectiveQuery = useEffectiveQuery();
    const {score, handleScoreChange} = useDocumentFeedback({docId, feedback, userId: user?.uid});
    const highlightsBySegment = useHighlightsBySegment(savedHighlights);

    // Delegate tag state/ops to hook
    const {
        tags,
        createTag,
        voteTag,
        removeTag,
        isVoted,
        isPosting,
        loadingTagOps
    } = useDocumentTags({incomingTags, docId, docUrl, docTitle, user});

    // For entity fetching, we need segments with proper IDs
    const segmentsToFetch = useMemo(() => {
        return displaySegments.filter(s => s && s.segmentId != null);
    }, [displaySegments]);

    const {entitiesMap} = useMultipleSegmentEntities(segmentsToFetch);

    // Optimistic local overrides: after a manual link succeeds we flip the affected mention to
    // linked (blue) immediately without refetching. Keyed by `${segmentId}:${startOffset}`.
    const [linkedOverrides, setLinkedOverrides] = useState({});

    const handleManualLink = useCallback(async ({entity, wikidataEntity}) => {
        const entityRef = String(wikidataEntity?.id || wikidataEntity?.entityId || wikidataEntity?.entityRef || '').trim();
        const segmentId = entity?.segmentId;
        if (!entityRef || segmentId == null) return;
        const isLocal = isLocalEntityRef(entityRef);
        await linkSegmentEntity({
            segmentId,
            mentionText: entity.mentionText ?? entity.canonicalName ?? '',
            startOffset: entity.startOffset,
            endOffset: entity.endOffset,
            ...(isLocal
                ? {localEntityRef: entityRef}
                : {wikidataId: entityRef}),
            entityType: entity.entityType || wikidataEntity?.entityType || null,
            canonicalName: wikidataEntity?.label || wikidataEntity?.canonicalName || entity.canonicalName || null,
        });
        setLinkedOverrides(prev => ({
            ...prev,
            [`${segmentId}:${entity.startOffset}`]: {
                wikidataId: entityRef,
                canonicalName: wikidataEntity?.label || wikidataEntity?.canonicalName || entity.canonicalName || null,
            },
        }));
    }, []);

    const {
        entityDialogState,
        handleEntityClick,
        handleCloseEntityDialog,
        searchState,
        handleCloseSearch,
        handleSelectSearchEntity,
    } = useEntityDialog(handleManualLink);

    // Tag the entities with their segmentId (needed by the manual-link handler) and apply any
    // optimistic linked overrides so a just-linked mention renders blue right away.
    const enrichedEntitiesMap = useMemo(() => {
        const out = new Map();
        entitiesMap.forEach((entities, segmentId) => {
            out.set(segmentId, (entities || []).map((e) => {
                const override = linkedOverrides[`${segmentId}:${e.startOffset}`];
                return {
                    ...e,
                    segmentId,
                    ...(override ? {wikidataId: override.wikidataId, canonicalName: override.canonicalName || e.canonicalName} : {}),
                };
            }));
        });
        return out;
    }, [entitiesMap, linkedOverrides]);

    // Inject CSS styles when in HTML mode
    useCssStylesInjection(viewMode, cssStyles, docId);

    // Use hook to scroll to segment and apply highlights
    const segmentsToHighlight = viewMode === 'html' && htmlSegments ? htmlSegments : displaySegments;
    useScrollToSegment(effectiveSegmentIndex, searchResultSegmentIds, segmentsToHighlight);

    const handleViewModeChange = useCallback((e) => {
        setViewMode(e.target.checked ? 'text' : 'html');
    }, [setViewMode]);

    if (!props) return null;

    return (
        <Stack spacing={2}>
            <DocumentHeader
                docTitle={docTitle}
                effectiveQuery={effectiveQuery}
                score={score}
                onScoreChange={handleScoreChange}
                classificationLevel={classificationLevel}
                docUrl={docUrl}
                docSource={docSource}
                sourceScore={sourceScore}
                docSourceType={docSourceType}
                hasHTML={hasHTML}
                viewMode={viewMode}
                onViewModeChange={handleViewModeChange}
                loadingHtml={loadingHtml}
                tags={tags}
                createTag={createTag}
                voteTag={voteTag}
                removeTag={removeTag}
                isVoted={isVoted}
                isPosting={isPosting}
                loadingTagOps={loadingTagOps}
                docId={docId}
            />

            <MentionHoverDismissProvider>
                <SegmentList
                    viewMode={viewMode}
                    htmlSegments={htmlSegments}
                    displaySegments={displaySegments}
                    highlightsBySegment={highlightsBySegment}
                    entitiesMap={enrichedEntitiesMap}
                    effectiveQuery={effectiveQuery}
                    onEntityClick={handleEntityClick}
                />
            </MentionHoverDismissProvider>

            <EntityDetailsDialog
                open={entityDialogState.open}
                anchorEl={entityDialogState.anchorEl}
                entity={entityDialogState.entity}
                onClose={handleCloseEntityDialog}
            />

            <WikidataEntitySearchDialog
                open={searchState.open}
                initialQuery={searchState.query}
                onSelect={handleSelectSearchEntity}
                onClose={handleCloseSearch}
            />
        </Stack>
    );
});

export default DocumentContent;

